import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Modal } from '../../components/common/Modal';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

interface Client {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
}

interface ProjectRequirements {
    references: string[];
    specifications?: string;
}

interface DesignProject {
    id: string;
    title: string;
    description: string;
    type: 'LIVERY' | 'LOGO' | 'TSHIRT' | 'CUSTOM';
    status: 'QUOTE' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
    budget: string;
    clientId: number;
    client?: Client;
    requirements?: ProjectRequirements;
    createdAt: string;
    updatedAt: string;
}

interface ProjectFormData {
    title: string;
    description: string;
    type: DesignProject['type'];
    status: DesignProject['status'];
    budget: string;
    clientId: number;
    client?: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
    };
    requirements?: {
        references: string[];
        specifications?: string;
    };
}

const initialFormData: ProjectFormData = {
    title: '',
    description: '',
    type: 'LIVERY',
    status: 'QUOTE',
    budget: '',
    clientId: 0,
    client: {
        id: 0,
        email: '',
        firstName: '',
        lastName: ''
    },
    requirements: {
        references: [],
        specifications: ''
    }
};

const ProjectDetailsContent: React.FC<{ project: DesignProject }> = ({ project }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">Project Details</h4>
                <dl className="space-y-3">
                    <div>
                        <dt className="text-sm font-medium text-gray-900">Type</dt>
                        <dd className="mt-1 text-sm text-gray-900">{project.type}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-gray-900">Status</dt>
                        <dd className="mt-1 text-sm text-gray-900">{project.status}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-gray-900">Budget</dt>
                        <dd className="mt-1 text-sm text-gray-900">{project.budget || 'Not specified'}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-gray-900">Created</dt>
                        <dd className="mt-1 text-sm text-gray-900">{new Date(project.createdAt).toLocaleString()}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-gray-900">Last Updated</dt>
                        <dd className="mt-1 text-sm text-gray-900">{new Date(project.updatedAt).toLocaleString()}</dd>
                    </div>
                </dl>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">Client Information</h4>
                <dl className="space-y-3">
                    <div>
                        <dt className="text-sm font-medium text-gray-900">Name</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                            {project.client ? `${project.client.firstName} ${project.client.lastName}` : 'N/A'}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-gray-900">Email</dt>
                        <dd className="mt-1 text-sm text-gray-900">{project.client?.email || 'N/A'}</dd>
                    </div>
                </dl>
            </div>

            <div className="col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">Project Description</h4>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{project.description}</p>
            </div>

            {project.requirements && (
                <div className="col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4">Requirements</h4>
                    {project.requirements.specifications && (
                        <div className="mb-4">
                            <h5 className="text-sm font-medium text-gray-900 mb-2">Specifications</h5>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                {project.requirements.specifications}
                            </p>
                        </div>
                    )}
                    {project.requirements.references && project.requirements.references.length > 0 && (
                        <div>
                            <h5 className="text-sm font-medium text-gray-900 mb-2">Reference Files</h5>
                            <ul className="grid grid-cols-2 gap-4">
                                {project.requirements.references.map((ref, index) => (
                                    <li key={index} className="flex items-center">
                                        <a 
                                            href={ref} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-purple-deep hover:text-purple-accent flex items-center"
                                        >
                                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                            Reference {index + 1}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export const ProjectsPage: React.FC = () => {
    const [projects, setProjects] = useState<DesignProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [selectedProject, setSelectedProject] = useState<DesignProject | null>(null);
    const [showProjectForm, setShowProjectForm] = useState(false);
    const [editingProject, setEditingProject] = useState<DesignProject | null>(null);
    const [formData, setFormData] = useState<ProjectFormData>(initialFormData);

    console.log('ProjectsPage - Initial render');

    const fetchProjects = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('ProjectsPage - Fetching projects with filters:', { statusFilter, typeFilter });
            const response = await api.design.getAllProjects({
                status: statusFilter !== 'all' ? statusFilter : undefined,
                type: typeFilter !== 'all' ? typeFilter : undefined
            });
            console.log('ProjectsPage - Projects response:', response);
            if (response && response.projects) {
                setProjects(response.projects);
                console.log('ProjectsPage - Projects updated:', response.projects);
            } else {
                console.error('ProjectsPage - Invalid response format:', response);
                throw new Error('Invalid response format');
            }
        } catch (err) {
            console.error('ProjectsPage - Error fetching projects:', err);
            setError('Failed to load projects. Please try again.');
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('ProjectsPage - useEffect triggered');
        fetchProjects();
    }, [statusFilter, typeFilter]);

    const handleStatusChange = async (projectId: string, newStatus: DesignProject['status']) => {
        try {
            await api.design.updateProject(projectId, { status: newStatus });
            fetchProjects(); // Refresh the list
        } catch (error) {
            console.error('Error updating project status:', error);
            setError('Failed to update project status');
        }
    };

    const handleEditProject = (project: DesignProject) => {
        setEditingProject(project);
        setFormData({
            title: project.title,
            description: project.description,
            type: project.type,
            status: project.status,
            budget: project.budget,
            clientId: project.clientId,
            client: project.client,
            requirements: project.requirements
        });
        setShowProjectForm(true);
    };

    const handleDeleteProject = async (id: string) => {
        try {
            await fetch(`/api/design/projects/${id}`, { method: 'DELETE' });
            setProjects(prev => prev.filter(project => project.id !== id));
        } catch (err) {
            console.error('Error deleting project:', err);
            setError('Failed to delete project');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const method = editingProject ? 'PUT' : 'POST';
            const url = editingProject ? `/api/design/projects/${editingProject.id}` : '/api/design/projects';

            const response = await api.design.updateProject(editingProject ? editingProject.id : '', formData);
            
            if (editingProject) {
                setProjects(prev => prev.map(project => 
                    project.id === editingProject.id ? { ...project, ...response } : project
                ));
            } else {
                setProjects(prev => [...prev, response]);
            }

            setShowProjectForm(false);
            setEditingProject(null);
            setFormData(initialFormData);
        } catch (err) {
            console.error('Error saving project:', err);
            setError('Failed to save project');
        }
    };

    const filteredProjects = projects.filter(project => {
        const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
        const matchesType = typeFilter === 'all' || project.type === typeFilter;
        return matchesStatus && matchesType;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-red-400 text-center py-8">
                <p>{error}</p>
                <button
                    onClick={fetchProjects}
                    className="mt-4 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-semibold text-teal-300">Design Projects</h1>
                <button
                    onClick={() => {
                        setEditingProject(null);
                        setFormData(initialFormData);
                        setShowProjectForm(true);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors"
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Add Project
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                    <div
                        key={project.id}
                        className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 overflow-hidden"
                    >
                        <div className="p-4">
                            <div className="flex justify-between items-start">
                                <h3 className="text-lg font-medium text-gray-200">{project.title}</h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditProject(project);
                                        }}
                                        className="text-teal-300 hover:text-teal-200"
                                    >
                                        <PencilSquareIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteProject(project.id);
                                        }}
                                        className="text-red-400 hover:text-red-300"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            <div className="mt-2 flex gap-2">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                                    {project.status}
                                </span>
                            </div>

                            <div className="mt-4">
                                <img
                                    src={project.description}
                                    alt={project.title}
                                    className="w-full h-48 object-cover rounded-md"
                                />
                            </div>

                            <div className="mt-4 text-sm text-gray-400">
                                <p>{project.description}</p>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-teal-300">Created:</span>
                                    <span className="ml-2 text-gray-300">
                                        {new Date(project.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-teal-300">Last Updated:</span>
                                    <span className="ml-2 text-gray-300">
                                        {new Date(project.updatedAt).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-4">
                                <span className="text-teal-300 text-sm">Client:</span>
                                <div className="mt-2 text-gray-300">
                                    {project.client ? `${project.client.firstName} ${project.client.lastName}` : 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Modal
                isOpen={showProjectForm}
                onClose={() => setShowProjectForm(false)}
                title={editingProject ? 'Edit Project' : 'Add Project'}
            >
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-teal-300">Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-teal-300">Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={3}
                                className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-teal-300">Type</label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value as DesignProject['type'] })}
                                className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                required
                            >
                                <option value="LIVERY">Racing Livery</option>
                                <option value="LOGO">Logo Design</option>
                                <option value="TSHIRT">T-Shirt Design</option>
                                <option value="CUSTOM">Custom Graphics</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-teal-300">Status</label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value as DesignProject['status'] })}
                                className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                required
                            >
                                <option value="QUOTE">Quote</option>
                                <option value="IN_PROGRESS">In Progress</option>
                                <option value="REVIEW">Review</option>
                                <option value="COMPLETED">Completed</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-teal-300">Budget</label>
                            <input
                                type="text"
                                value={formData.budget}
                                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                                className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-teal-300">Client</label>
                            <div className="mt-2 space-y-2">
                                <div>
                                    <input
                                        type="text"
                                        value={formData.client?.firstName || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            client: {
                                                ...formData.client!,
                                                firstName: e.target.value
                                            }
                                        })}
                                        className="block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        value={formData.client?.lastName || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            client: {
                                                ...formData.client!,
                                                lastName: e.target.value
                                            }
                                        })}
                                        className="block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        value={formData.client?.email || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            client: {
                                                ...formData.client!,
                                                email: e.target.value
                                            }
                                        })}
                                        className="block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-teal-300">Requirements</label>
                            <div className="mt-2 space-y-2">
                                <div>
                                    <label className="block text-sm font-medium text-teal-300">Specifications</label>
                                    <textarea
                                        value={formData.requirements?.specifications || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            requirements: {
                                                ...formData.requirements!,
                                                specifications: e.target.value
                                            }
                                        })}
                                        rows={3}
                                        className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-teal-300">References</label>
                                    <textarea
                                        value={formData.requirements?.references.join('\n') || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            requirements: {
                                                ...formData.requirements!,
                                                references: e.target.value.split('\n').map(ref => ref.trim())
                                            }
                                        })}
                                        rows={3}
                                        className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-600">
                        <button
                            type="button"
                            onClick={() => setShowProjectForm(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                        >
                            {editingProject ? 'Update Project' : 'Create Project'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

function getStatusColor(status: DesignProject['status']) {
    switch (status) {
        case 'QUOTE':
            return 'bg-yellow-900/50 text-yellow-300';
        case 'IN_PROGRESS':
            return 'bg-blue-900/50 text-blue-300';
        case 'REVIEW':
            return 'bg-purple-900/50 text-purple-300';
        case 'COMPLETED':
            return 'bg-green-900/50 text-green-300';
        case 'CANCELLED':
            return 'bg-red-900/50 text-red-300';
        default:
            return 'bg-gray-900/50 text-gray-300';
    }
} 